import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translations } from '../lib/i18n';
import { getBuyerCart } from '../lib/api';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('luxortek_lang') || 'en';
  });
  
  const [requestList, setRequestList] = useState(() => {
    const saved = localStorage.getItem('luxortek_request');
    return saved ? JSON.parse(saved) : [];
  });

  const [adminToken, setAdminToken] = useState(() => {
    return localStorage.getItem('luxortek_admin_token') || null;
  });

  const [adminInfo, setAdminInfo] = useState(() => {
    const saved = localStorage.getItem('luxortek_admin_info');
    return saved ? JSON.parse(saved) : null;
  });

  // Buyer state
  const [buyerToken, setBuyerToken] = useState(() => {
    return localStorage.getItem('luxortek_buyer_token') || null;
  });

  const [buyerInfo, setBuyerInfo] = useState(() => {
    const saved = localStorage.getItem('luxortek_buyer_info');
    return saved ? JSON.parse(saved) : null;
  });

  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    localStorage.setItem('luxortek_lang', language);
  }, [language]);

  useEffect(() => {
    localStorage.setItem('luxortek_request', JSON.stringify(requestList));
  }, [requestList]);

  useEffect(() => {
    if (adminToken) {
      localStorage.setItem('luxortek_admin_token', adminToken);
    } else {
      localStorage.removeItem('luxortek_admin_token');
    }
  }, [adminToken]);

  useEffect(() => {
    if (adminInfo) {
      localStorage.setItem('luxortek_admin_info', JSON.stringify(adminInfo));
    } else {
      localStorage.removeItem('luxortek_admin_info');
    }
  }, [adminInfo]);

  useEffect(() => {
    if (buyerToken) {
      localStorage.setItem('luxortek_buyer_token', buyerToken);
    } else {
      localStorage.removeItem('luxortek_buyer_token');
    }
  }, [buyerToken]);

  useEffect(() => {
    if (buyerInfo) {
      localStorage.setItem('luxortek_buyer_info', JSON.stringify(buyerInfo));
    } else {
      localStorage.removeItem('luxortek_buyer_info');
    }
  }, [buyerInfo]);

  const isBuyer = !!buyerToken;
  const isBuyerApproved = buyerInfo?.role === 'buyer_approved';

  const refreshCart = useCallback(async () => {
    if (!isBuyerApproved) {
      setCartCount(0);
      return;
    }
    try {
      const res = await getBuyerCart();
      setCartCount(res.data.items?.length || 0);
    } catch (err) {
      console.error('Failed to fetch cart count:', err);
    }
  }, [isBuyerApproved]);

  useEffect(() => {
    if (isBuyerApproved) {
      refreshCart();
    } else {
      setCartCount(0);
    }
  }, [isBuyerApproved, refreshCart]);

  const t = (path) => {
    const keys = path.split('.');
    let result = translations[language] || translations.en;
    for (const key of keys) {
      result = result?.[key];
    }
    return result || path;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'es' : 'en');
  };

  const addToRequest = (product) => {
    setRequestList(prev => {
      const existing = prev.find(item => item.product_id === product.id);
      if (existing) return prev;
      return [...prev, {
        product_id: product.id,
        unique_id: product.unique_id,
        oem: product.oem,
        model: product.model,
        sale_price: product.sale_price,
        quantity: 1,
      }];
    });
  };

  const removeFromRequest = (productId) => {
    setRequestList(prev => prev.filter(item => item.product_id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromRequest(productId);
      return;
    }
    setRequestList(prev =>
      prev.map(item =>
        item.product_id === productId ? { ...item, quantity } : item
      )
    );
  };

  const clearRequest = () => {
    setRequestList([]);
  };

  const login = (token, admin) => {
    setAdminToken(token);
    setAdminInfo(admin);
  };

  const logout = () => {
    setAdminToken(null);
    setAdminInfo(null);
  };

  const buyerLogin = (token, buyer) => {
    setBuyerToken(token);
    setBuyerInfo(buyer);
  };

  const buyerLogout = () => {
    setBuyerToken(null);
    setBuyerInfo(null);
    setCartCount(0);
  };

  const isAdmin = !!adminToken;

  const getOrderDiscount = (totalUnits) => {
    if (totalUnits >= 100) return 0.06;
    if (totalUnits >= 50) return 0.04;
    if (totalUnits >= 10) return 0.02;
    return 0.0;
  };

  const totalUnitsInCart = requestList.reduce((s, i) => s + (i.quantity || 1), 0);
  const orderDiscountPct = getOrderDiscount(totalUnitsInCart);

  const applyVolumeDiscount = (salePrice, _qty) => {
    // Order-level discount: uses totalUnitsInCart, not per-SKU qty
    if (!salePrice || salePrice <= 0) return { discount: 0, unitPrice: 0 };
    const unitPrice = Math.round(salePrice * (1 - orderDiscountPct));
    return { discount: orderDiscountPct, unitPrice };
  };

  const requestTotal = requestList.reduce((sum, item) => {
    const qty = item.quantity || 1;
    const basePrice = item.sale_price || 0;
    const unitPrice = Math.round(basePrice * (1 - orderDiscountPct));
    return sum + (unitPrice * qty);
  }, 0);
  const requestCount = requestList.length;

  return (
    <AppContext.Provider value={{
      language,
      setLanguage,
      toggleLanguage,
      t,
      requestList,
      addToRequest,
      removeFromRequest,
      updateQuantity,
      applyVolumeDiscount,
      orderDiscountPct,
      totalUnitsInCart,
      getOrderDiscount,
      clearRequest,
      requestTotal,
      requestCount,
      adminToken,
      adminInfo,
      login,
      logout,
      isAdmin,
      buyerToken,
      buyerInfo,
      buyerLogin,
      buyerLogout,
      isBuyer,
      isBuyerApproved,
      cartCount,
      refreshCart,
    }}>
      {children}
    </AppContext.Provider>
  );
};
